import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  Text,
  Modal,
  ModalFooterButtons,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  TextInput,
  useToast,
} from '@televet/kibble-ui';
import { WorkflowEventTriggerType } from 'shared/types/graphql';
import { WorkflowEvent as AutomationType } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { NextStepSelectValue } from 'pages/Automations/types/NextStepSelectValue';
import { useCreateAutomation } from '../../hooks/useCreateAutomation';

interface AddNewAutomationModalProps {
  handleUpdateSelection: ({ value }: { value: NextStepSelectValue | string }) => void;
  isOpen: boolean;
  onClose: () => void;
}

export const AddNewAutomationModal = ({
  isOpen,
  onClose,
  handleUpdateSelection,
}: AddNewAutomationModalProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [isFieldInvalid, setIsFieldInvalid] = useState<boolean>(false);
  const [automationName, setAutomationName] = useState<string>('');
  const toast = useToast();

  const { createAutomation, isLoading: isSaving } = useCreateAutomation();

  const handleSubmit = async (): Promise<void> => {
    if (!automationName?.length) {
      setIsFieldInvalid(true);
      return;
    }

    try {
      const newAutomation = await createAutomation({
        workflowEventSettingData: {
          name: automationName,
          event: AutomationType.AppointmentReminder, // This is required to create an automation, default to reminder type as it's the most universal
          triggerType: WorkflowEventTriggerType.Manual,
          clinic: {
            connect: {
              id: currentClinicId,
            },
          },
          isPublished: false,
        },
      });

      if (!newAutomation?.id) {
        throw new Error('Failed to create automation');
      }

      handleUpdateSelection({ value: newAutomation.id });
      toast({
        status: 'success',
        title: 'Success!',
        description: 'Automation created successfully.',
      });

      onClose();
    } catch (error) {
      console.error('Failed to create automation', error);
      toast({
        status: 'error',
        title: 'Error!',
        description: 'There was an error when attempting to create this automation.',
      });
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setAutomationName(e.target.value);
  };

  return (
    <Modal size="md" onClose={onClose} isOpen={isOpen}>
      <ModalHeader>Add New Automation</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text>Selecting ‘Save’ will create a new automation and add it as the next step for this prompt. </Text>
        <Text fontWeight="bold">
          Don’t forget to edit and activate your new automation before activating this automation!
        </Text>
        <Box mt={5}>
          <TextInput
            autoFocus
            isInvalid={isFieldInvalid}
            onChange={handleInputChange}
            placeholder="New Automation"
            errorText="Please enter an automation name"
            label="Automation Name"
          />
        </Box>
      </ModalBody>
      <ModalFooterButtons submitText="Save" isLoading={isSaving} onSubmit={handleSubmit} />
    </Modal>
  );
};
